import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { useSubscription } from '../../contexts/SubscriptionContext';
import useAuth from '../../hooks/useAuth';

const PaymentSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, refreshUserProfile } = useAuth();
  const { refetchUserData } = useSubscription();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    const handlePaymentSuccess = async () => {
      if (!user) {
        setError('User not authenticated');
        setLoading(false);
        return;
      }

      try {
        // If no session ID, redirect user after a short delay
        if (!sessionId) {
          // Wait a moment to ensure we're actually in a payment success context
          await new Promise(resolve => setTimeout(resolve, 1000));
          
          // Refresh user profile to get updated subscription_tier
          await refreshUserProfile();
          
          // Try to refresh subscription data anyway (webhook might have processed it)
          await refetchUserData();
          setLoading(false);
          return;
        }

        // If we have a session ID, verify it and update subscription
        // Wait a moment for webhook to process
        await new Promise(resolve => setTimeout(resolve, 3000));
        
        // Refresh user profile to get updated subscription_tier from the webhook update
        await refreshUserProfile();
        
        // Refresh subscription data
        await refetchUserData();
        
        setLoading(false);
      } catch (err) {
        console.error('Error processing payment success:', err);
        // Still show success page even if refresh fails - webhook will handle it
        setLoading(false);
      }
    };

    handlePaymentSuccess();
  }, [sessionId, user, refetchUserData, refreshUserProfile]);

  const handleContinue = () => {
    navigate('/employer-dashboard');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
          <Loader2 className="mx-auto h-12 w-12 text-blue-600 animate-spin mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Processing Payment...
          </h2>
          <p className="text-gray-600">
            Please wait while we confirm your subscription.
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
          <AlertCircle className="mx-auto h-12 w-12 text-red-600 mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Payment Processing Error
          </h2>
          <p className="text-gray-600 mb-6">
            {error}
          </p>
          <div className="space-y-3">
            <button
              onClick={() => navigate('/pricing')}
              className="w-full text-white py-2 px-4 rounded-lg hover:opacity-90 transition-opacity"
              style={{ background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)' }}
            >
              Return to Pricing
            </button>
            <button
              onClick={() => navigate('/employer-dashboard')}
              className="w-full bg-gray-600 text-white py-2 px-4 rounded-lg hover:bg-gray-700 transition-colors"
            >
              Go to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
        <CheckCircle className="mx-auto h-16 w-16 text-green-600 mb-6" />
        
        <h1 className="text-2xl font-bold text-gray-900 mb-2">
          Payment Successful!
        </h1>
        
        <p className="text-gray-600 mb-6">
          Thank you for subscribing to VA Sprouts. Your subscription has been activated 
          and you now have access to all premium features.
        </p>

        <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
          <h3 className="font-semibold text-green-800 mb-2">What's Next?</h3>
          <ul className="text-left text-green-700 space-y-1">
            <li>• Access premium job posting features</li>
            <li>• Hire more virtual assistants</li>
            <li>• Use AI-powered recommendations</li>
            <li>• Manage your subscription anytime</li>
          </ul>
        </div>

        <div className="space-y-3">
          <button
            onClick={handleContinue}
            className="w-full text-white py-3 px-4 rounded-lg hover:opacity-90 transition-opacity font-semibold"
            style={{ background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)' }}
          >
            Continue to Dashboard
          </button>
          
          <button
            onClick={() => navigate('/pricing')}
            className="w-full text-gray-600 py-2 px-4 rounded-lg hover:bg-gray-50 transition-colors"
          >
            View Pricing Plans
          </button>
        </div>

        <div className="mt-6 pt-6 border-t border-gray-200">
          <p className="text-sm text-gray-500">
            Questions about your subscription?{' '}
            <a 
              href="/help-support" 
              className="text-blue-600 hover:text-blue-700"
            >
              Contact Support
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccessPage;